import React from 'react';

/**
 * SiteSift.AI brand assets — uses the official AI-generated logo PNG.
 * Source: public/brand/sitesift-logo.png (generated 2026-05-06 via Gemini Nano Banana
 * by reference-matching the approved design — wordmark with magnifying-glass-as-S +
 * cyan accent dots + cyan halo + tagline "Automate Outreach. Accelerate Deals.").
 *
 * Three exports (same API as before — drop-in compatible):
 *   - <SiteSiftLogo height={32} />            horizontal wordmark (default)
 *   - <SiteSiftIcon size={32} />              square crop, magnifying-glass region only
 *   - <SiteSiftLogoStacked height={88} />     wordmark + tagline (alias for SiteSiftLogo)
 *
 * The PNG is a square canvas with the actual mark centered inside it. Compact
 * UI slots crop to the wordmark bounds so the logo remains legible.
 */

const LOGO_SRC = '/brand/sitesift-logo.png';
const WORDMARK_DARK_SRC = '/brand/sitesift-wordmark-dark-balanced.png';
const WORDMARK_LIGHT_SRC = '/brand/sitesift-wordmark-light-balanced.png';
const SOURCE_SIZE = 1024;
const WORDMARK_CROP = { x: 175, y: 365, width: 710, height: 200 };
const FULL_CROP = { x: 182, y: 375, width: 660, height: 262 };

const WORDMARK_ASPECT = 669 / 291;

function croppedLogoStyles(height, crop, style) {
  if (typeof height !== 'number') {
    return {
      wrapperStyle: { height, display: 'inline-block', overflow: 'hidden', ...style },
      imgStyle: { height, display: 'block' },
    };
  }

  const scale = height / crop.height;
  const width = Math.round(crop.width * scale);
  const imageSize = Math.round(SOURCE_SIZE * scale);

  return {
    wrapperStyle: {
      width,
      height,
      display: 'inline-block',
      overflow: 'hidden',
      verticalAlign: 'middle',
      flexShrink: 0,
      borderRadius: 4,
      ...style,
    },
    imgStyle: {
      width: imageSize,
      height: imageSize,
      display: 'block',
      maxWidth: 'none',
      transform: `translate(${-Math.round(crop.x * scale)}px, ${-Math.round(crop.y * scale)}px)`,
      transformOrigin: 'top left',
    },
  };
}

export default function SiteSiftLogo({ height = 32, className = '', ariaLabel = 'SiteSift.AI', noTagline = true, style = {}, tone = 'dark' }) {
  if (noTagline) {
    const numericHeight = typeof height === 'number';
    const wordmarkStyle = {
      width: numericHeight ? Math.round(height * WORDMARK_ASPECT) : 'auto',
      height,
      display: 'inline-block',
      verticalAlign: 'middle',
      flexShrink: 0,
      ...style,
    };

    return (
      <span className={className} style={wordmarkStyle} role="img" aria-label={ariaLabel}>
        <img
          src={tone === 'light' ? WORDMARK_LIGHT_SRC : WORDMARK_DARK_SRC}
          alt={ariaLabel}
          style={{ width: '100%', height: '100%', display: 'block', objectFit: 'contain' }}
        />
      </span>
    );
  }

  const crop = noTagline ? WORDMARK_CROP : FULL_CROP;
  const { wrapperStyle, imgStyle } = croppedLogoStyles(height, crop, style);

  return (
    <span className={className} style={wrapperStyle} role="img" aria-label={ariaLabel}>
      <img src={LOGO_SRC} alt={ariaLabel} style={imgStyle} />
    </span>
  );
}

// Square icon — for now, falls back to the same logo cropped to a square magnifying-glass region.
// TODO: generate a dedicated square icon PNG (favicon-ready) in a follow-up.
export function SiteSiftIcon({ size = 32, className = '', ariaLabel = 'SiteSift' }) {
  return (
    <span
      className={className}
      style={{ width: size, height: size, display: 'inline-block', overflow: 'hidden', borderRadius: 4 }}
      role="img"
      aria-label={ariaLabel}
    >
      {/* Show the magnifying-glass portion of the wide logo: roughly the middle 30% */}
      <img
        src={LOGO_SRC}
        alt={ariaLabel}
        style={{ height: size, width: 'auto', display: 'block', objectPosition: '-' + Math.round(size * 1.4) + 'px 0', objectFit: 'none' }}
      />
    </span>
  );
}

// Stacked = full logo (with tagline). Alias for SiteSiftLogo with noTagline=false.
export function SiteSiftLogoStacked({ height = 88, className = '', ariaLabel = 'SiteSift.AI — Automate Outreach. Accelerate Deals.', style = {} }) {
  return <SiteSiftLogo height={height} className={className} ariaLabel={ariaLabel} noTagline={false} style={style} />;
}
